import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import cogoToast from '@successtar/cogo-toast'

interface GalleryImage {
  url: string
  thumbnailUrl?: string
  key: string
  type: 'image' | 'strip' | 'video' | 'gif' | 'recording'
  originalUrl?: string // Store original CDN URL for blob URL recreation
}

interface GallerySelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (photoUrls: string[]) => void
  maxSelectable: number
  filterType?: 'image' | 'strip'
}

const GallerySelectionModal: React.FC<GallerySelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  maxSelectable,
  filterType = 'image'
}) => {
  const { t } = useTranslation()
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const observerTarget = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  // Cleanup blob URLs when modal closes or component unmounts
  useEffect(() => {
    return (): void => {
      images.forEach((img: GalleryImage): void => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url)
        }
        if (img.thumbnailUrl && img.thumbnailUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.thumbnailUrl)
        }
      })
    }
  }, [images])

  // Convert a single image to blob URL (progressive loading)
  // This avoids CORS issues by converting CDN URLs to local blob URLs
  const convertImageToBlob = async (img: GalleryImage): Promise<GalleryImage> => {
    try {
      // Try to fetch the image and convert to blob URL
      // Don't use explicit { mode: 'cors' } - let browser handle it naturally
      const imgResponse = await fetch(img.url)
      if (imgResponse.ok) {
        const blob = await imgResponse.blob()
        const blobUrl = URL.createObjectURL(blob)

        let thumbnailBlobUrl: string | undefined = undefined
        if (img.thumbnailUrl) {
          try {
            const thumbResponse = await fetch(img.thumbnailUrl)
            if (thumbResponse.ok) {
              const thumbBlob = await thumbResponse.blob()
              thumbnailBlobUrl = URL.createObjectURL(thumbBlob)
            }
          } catch (thumbError) {
            console.warn('Failed to load thumbnail:', img.thumbnailUrl, thumbError)
            // Use main blob URL as thumbnail fallback
            thumbnailBlobUrl = blobUrl
          }
        }

        return {
          ...img,
          url: blobUrl,
          thumbnailUrl: thumbnailBlobUrl || blobUrl,
          originalUrl: img.originalUrl || img.url // Preserve original URL if exists, otherwise use current
        }
      } else {
        // Fallback to original URL if fetch fails
        console.warn('Failed to fetch image:', img.url)
        return img
      }
    } catch (error) {
      console.warn('Failed to convert image to blob:', img.url, error)
      // Fallback to original URL
      return img
    }
  }

  const fetchGalleryImages = useCallback(
    async (page: number = 1, shouldAppend: boolean = false): Promise<void> => {
      if (isFetchingRef.current) {
        return
      }
      isFetchingRef.current = true

      if (shouldAppend) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      try {
        const response = await fetch(
          `${process.env.VITE_API_URL}/api/gallery?type=${filterType}&limit=50&sortBy=date-desc&page=${page}`,
          {
            credentials: 'include',
            cache: 'no-cache'
          }
        )

        if (response.ok) {
          const data: {
            images?: GalleryImage[]
            hasMore?: boolean
            currentPage?: number
          } = await response.json()

          if (data.images && Array.isArray(data.images)) {
            // Filter to only include the specified type
            const imageItems = data.images.filter(
              (img: GalleryImage) => img.type === filterType && img.url
            )

            if (shouldAppend) {
              // Append new images and convert them progressively
              setImages((prev) => {
                const newImages = [...prev]
                imageItems.forEach((img) => {
                  // Store original URL before conversion
                  const imgWithOriginal = { ...img, originalUrl: img.url }
                  // Add image immediately with original URL, then convert to blob
                  newImages.push(imgWithOriginal)
                  convertImageToBlob(imgWithOriginal).then((convertedImg) => {
                    setImages((current) =>
                      current.map((i) => (i.key === convertedImg.key ? convertedImg : i))
                    )
                  })
                })
                return newImages
              })
            } else {
              // Set initial images with original URLs stored, then convert them progressively
              const imagesWithOriginal = imageItems.map((img) => ({ ...img, originalUrl: img.url }))
              setImages(imagesWithOriginal)
              // Convert images one by one and update state as each completes
              imagesWithOriginal.forEach((img) => {
                convertImageToBlob(img).then((convertedImg) => {
                  setImages((current) =>
                    current.map((i) => (i.key === convertedImg.key ? convertedImg : i))
                  )
                })
              })
            }

            setHasMore(data.hasMore ?? false)
            setCurrentPage(data.currentPage ?? page)
          } else {
            if (!shouldAppend) {
              setImages([])
            }
            setHasMore(false)
          }
        } else {
          console.error('Failed to fetch gallery images')
          if (!shouldAppend) {
            cogoToast.error(t('strip.galleryModal.errorFetching', 'Failed to fetch photos'))
            setImages([])
          }
        }
      } catch (error) {
        console.error('Error fetching gallery images:', error)
        if (!shouldAppend) {
          cogoToast.error(t('strip.galleryModal.errorFetching', 'Failed to fetch photos'))
          setImages([])
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
        isFetchingRef.current = false
      }
    },
    [t, filterType]
  )

  // Reset and fetch when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setImages([])
      setSelectedKeys(new Set())
      setCurrentPage(1)
      setHasMore(true)
      fetchGalleryImages(1, false)
    }
  }, [isOpen, fetchGalleryImages])

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && hasMore && !loadingMore && !loading && !isFetchingRef.current) {
          fetchGalleryImages(currentPage + 1, true)
        }
      },
      { threshold: 0.1 }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return (): void => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [hasMore, loadingMore, loading, currentPage, fetchGalleryImages])

  const handlePhotoClick = (imageKey: string): void => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(imageKey)) {
        newSet.delete(imageKey)
      } else if (newSet.size < maxSelectable) {
        newSet.add(imageKey)
      } else {
        cogoToast.warn(
          t('strip.galleryModal.maxSelected', 'You can only select up to {{count}} photos', {
            count: maxSelectable
          })
        )
      }
      return newSet
    })
  }

  const handleConfirm = async (): Promise<void> => {
    const selectedKeysArray = Array.from(selectedKeys)
    if (selectedKeysArray.length === 0) {
      cogoToast.warn(t('strip.galleryModal.noSelection', 'Please select at least one photo'))
      return
    }

    // Create fresh blob URLs for selected images (matching edit project pattern)
    // This ensures blob URLs persist even after modal closes and avoids CORS issues
    const blobUrls = await Promise.all(
      selectedKeysArray.map(async (key) => {
        const image = images.find((img) => img.key === key)
        if (!image) return ''

        try {
          // Use original URL if available, otherwise use current URL
          const urlToFetch = image.originalUrl || image.url

          // If it's already a blob URL and we don't have original, use it directly
          // Otherwise fetch from CDN and create new blob URL
          if (urlToFetch.startsWith('blob:')) {
            // Create a new blob URL from existing blob to ensure persistence
            const response = await fetch(urlToFetch)
            const blob = await response.blob()
            return URL.createObjectURL(blob)
          } else {
            // Fetch from CDN and create blob URL (avoiding CORS issues)
            const imgResponse = await fetch(urlToFetch)
            if (imgResponse.ok) {
              const blob = await imgResponse.blob()
              return URL.createObjectURL(blob)
            } else {
              console.warn('Failed to fetch image:', urlToFetch)
              return urlToFetch
            }
          }
        } catch (error) {
          console.warn('Failed to create blob URL for selected image:', error)
          // Fallback to current URL
          return image.url
        }
      })
    )

    // Filter out any empty strings
    const validUrls = blobUrls.filter((url) => url !== '')

    if (validUrls.length === 0) {
      cogoToast.warn(t('strip.galleryModal.noSelection', 'Please select at least one photo'))
      return
    }

    onSelect(validUrls)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-carbon">
            {t('strip.galleryModal.title', 'Select Photos from Gallery')}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t('strip.galleryModal.selectUpTo', 'Select up to {{count}} photos', {
              count: maxSelectable
            })}
            {selectedKeys.size > 0 && (
              <span className="ml-2 font-semibold text-blue-500">
                ({selectedKeys.size}/{maxSelectable} selected)
              </span>
            )}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && images.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-500">
                  {t('strip.galleryModal.loading', 'Loading photos...')}
                </p>
              </div>
            </div>
          ) : images.length === 0 && !loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">{t('strip.galleryModal.noPhotos', 'No photos found')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => {
                  const isSelected = selectedKeys.has(image.key)
                  return (
                    <div key={image.key} className="relative group">
                      <button
                        type="button"
                        onClick={() => handlePhotoClick(image.key)}
                        className={`w-full relative overflow-hidden rounded-xl transition-all duration-300 ${
                          isSelected
                            ? 'ring-4 ring-blue-500 scale-95'
                            : 'hover:scale-[0.98] ring-2 ring-transparent hover:ring-gray-300'
                        }`}
                      >
                        <PhotoFrame
                          src={image.thumbnailUrl || image.url}
                          alt="Gallery Photo"
                          displayRatio={1}
                          className={isSelected ? 'opacity-75' : ''}
                        />
                        <div
                          className={`absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-300 ${
                            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          <div className="text-center text-white">
                            <div className="text-4xl mb-2">{isSelected ? '✓' : '+'}</div>
                            <p className="text-lg font-medium">
                              {isSelected
                                ? t('select.selected', 'Selected')
                                : t('select.clickToSelect', 'Click to Select')}
                            </p>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg bg-blue-500">
                            <span className="text-white text-lg font-bold">
                              {Array.from(selectedKeys).indexOf(image.key) + 1}
                            </span>
                          </div>
                        )}
                      </button>
                    </div>
                  )
                })}
              </div>
              {/* Loading more indicator and observer target */}
              <div
                ref={observerTarget}
                className={`py-8 flex justify-center ${loadingMore ? 'opacity-100' : 'opacity-0'}`}
              >
                {loadingMore && (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500">
                      {t('strip.galleryModal.loadingMore', 'Loading more photos...')}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <Button
            text={t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={onClose}
            className="py-1 px-3 text-sm"
          />
          <Button
            text={t('common.select', 'Select')}
            onClick={handleConfirm}
            disabled={selectedKeys.size === 0}
            variant={selectedKeys.size > 0 ? 'primary' : 'secondary'}
            className="py-1 px-3 text-sm"
          />
        </div>
      </div>
    </div>
  )
}

export default GallerySelectionModal
